import { Button, Icon, ToolTip } from '@greystar/designsystem';
import {
  backToFpe,
  calculateOneTimeFees,
  calculateTMLP,
  formatPriceDisplay,
  getLinkIcon,
  goBackToComponent,
  useAnalytics,
  useFeesAnalytics,
  useFormattedAddress,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';
import TmlpLabel from '../TmlpLabel/TmlpLabel';
import PricingTrayProps from './PricingTray.props';
import styles from './pricingTray.module.scss';

const PricingTray = (props: PricingTrayProps) => {
  const router = useRouter();
  const analytics = useAnalytics();
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const propertyId = property.id ?? '';
  const propertyName = property.name ?? '';
  const state = property?.location?.state ?? '';
  const property_location = useFormattedAddress(property.location);

  const { analyticsData, setAnalyticsData, mergeAnalyticsData } = useFeesAnalytics();

  const handleNextClick = () => {
    if (props.activeStep === 1) {
      props.setActiveStep(2);
      props.onNavigate();

      setAnalyticsData({ ...analyticsData, current_page: 'costBreakdown' });

      mergeAnalyticsData({
        overrides: {
          event: 'unit_rent_calc_fee_page_next',
          action_type: 'next',
          click_text: 'Next',
        },
        onComplete: (data) => {
          analytics.trackEvent('unit_rent_calc_fee_page_next', data);
        },
      });
    } else {
      if (!props.contact?.leaseOnlineUrl) {
        goBackToComponent(router, 'contact-us');
        mergeAnalyticsData({
          overrides: {
            event: 'unit_rent_calc_fee_page_entry',
            action_type: 'entry',
            click_text: 'Apply',
            destination_url: props.contact?.leaseOnlineUrl ?? '',
          },
          onComplete: (data) => {
            analytics.trackEvent('unit_rent_calc_apply', data);
          },
        });
      } else {
        props.setIsApplyModalOpen(true);
        mergeAnalyticsData({
          overrides: {
            event: 'unit_rent_calc_interstitial',
            action_type: 'interstitial',
            click_text: 'Apply',
            destination_url: '',
          },
          onComplete: (data) => {
            analytics.trackEvent('unit_rent_calc_interstitial', data);
          },
        });
      }
    }
  };

  const handlePrevClick = () => {
    if (props.activeStep === 1) {
      backToFpe(router, 'fpe-floor-plans');

      mergeAnalyticsData({
        overrides: {
          event: 'unit_rent_calc_fee_back',
          action_type: 'back',
          click_text: 'Back',
          click_type: 'internal',
          destination_url: '#fpe-floor-plans',
        },
        onComplete: (data) => {
          analytics.trackEvent('unit_rent_calc_fee_back', data);
        },
      });

      setAnalyticsData({ ...analyticsData, current_page: 'alternateLeaseTerm' });
    } else {
      props.setActiveStep(1);
      props.onNavigate();

      mergeAnalyticsData({
        overrides: {
          event: 'unit_rent_calc_cost_breakdown_back',
          action_type: 'back',
          click_text: 'Back',
          click_type: 'internal',
          destination_url: '/',
        },
        onComplete: (data) => {
          analytics.trackEvent('unit_rent_calc_cost_breakdown_back', data);
        },
      });

      setAnalyticsData({ ...analyticsData, current_page: 'fee' });
    }
  };

  const oneTimeFees = calculateOneTimeFees(props.feeState);

  const tmlp = calculateTMLP(
    props.feeState.selectedBaseRent,
    props.feeState.allFees,
    props.feeState.numberOfApplicants,
    props.feeState.selectedMoveInDate
  );

  const handleTooltipClick = (label: string) => {
    analytics.trackEvent('tooltip', {
      action_type: 'click',
      state: state,
      property_location,
      property_id: propertyId,
      property_name: propertyName,
      content_group: label,
      module_name: 'fees section',
      inpage_location: 'middle',
      click_text: label,
      destination_url: '',
    });
  };

  const updateTooltipData = (label = '') => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_tooltip',
        action_type: 'click',
        tooltip: label,
        click_text: label,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_tooltip', data);
      },
    });
  };

  return (
    <div className={styles['pricing-tray']}>
      <div className={styles['pricing-tray-pricing-wrapper']}>
        <div className={styles['pricing-tray-pricing-wrapper-inner']}>
          <div className={styles['pricing-tray-pricing-wrapper-inner-price']}>
            <span className={styles['pricing-tray-pricing-wrapper-inner-price-no-bold']}>
              {formatPriceDisplay(oneTimeFees.amount, oneTimeFees.maxAmount)}
            </span>
          </div>
          <div className={styles['pricing-tray-pricing-wrapper-inner-title']}>
            One time costs
            <ToolTip
              description={
                'Includes one-time charges due at the time of application, move-in, and/or move-out unless specified otherwise in the application or lease agreement.'
              }
              elementToHover={<Icon iconName="QuestionMark" size="sm" />}
              toolTipClick={() => {
                updateTooltipData('One time costs');
                handleTooltipClick('One time costs');
              }}
            />
          </div>
        </div>
        <div className={styles['pricing-tray-pricing-wrapper-inner']}>
          <div className={styles['pricing-tray-pricing-wrapper-inner-price']}>
            <span className={styles['pricing-tray-pricing-wrapper-inner-price-highlight']}>
              {formatPriceDisplay(tmlp.amount, tmlp.maxAmount)}
            </span>
          </div>
          <div className={`${styles['pricing-tray-pricing-wrapper-inner-title']}`}>
            Total monthly leasing price
            <ToolTip
              description={<TmlpLabel />}
              elementToHover={<Icon iconName="QuestionMark" size="sm" />}
              direction="left"
              tooltipId="tooltip-anchor"
              toolTipClick={() => {
                updateTooltipData('Total monthly leasing price');
                handleTooltipClick('Total monthly leasing price');
              }}
            />
          </div>
        </div>
      </div>
      <div className={styles['pricing-tray-btn-wrapper']}>
        <Button label="Back" variant="tertiary" onClick={handlePrevClick} />
        <Button
          onClick={handleNextClick}
          label={props.activeStep === 1 ? 'Next' : 'Apply'}
          variant="primary"
          icon={getLinkIcon()}
        />
      </div>
    </div>
  );
};

export default PricingTray;
